import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaInfoCircle } from 'react-icons/fa';

/**
 * Info icon that opens a formula panel with examples.
 * Place under a section title / at the bottom of a card.
 */
export function FormulaInfoHint({ title = 'How these values are calculated', items = [] }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!items.length) return null;

  const modal =
    open &&
    createPortal(
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
        onClick={() => setOpen(false)}
        role="presentation"
      >
        <div
          className="max-h-[85vh] w-full max-w-lg overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="formula-info-title"
        >
          <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
            <p id="formula-info-title" className="text-base font-semibold text-gray-900">
              {title}
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xl leading-none text-gray-500 hover:bg-gray-100"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <ul className="max-h-[calc(85vh-4.5rem)] space-y-3 overflow-y-auto px-5 py-4">
            {items.map((item) => (
              <li key={item.label} className="rounded-lg bg-[#faf8f5] px-3 py-2.5">
                <p className="text-xs font-semibold text-gray-900">{item.label}</p>
                <p className="mt-1 text-xs text-gray-700">
                  <span className="font-medium text-gray-800">Formula: </span>
                  {item.formula}
                </p>
                {item.example ? (
                  <p className="mt-1 text-xs text-gray-600">
                    <span className="font-medium text-gray-800">Eg: </span>
                    {item.example}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      </div>,
      document.body
    );

  return (
    <>
      <div className="my-1">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-[#FFF7F0] px-2.5 py-1.5 text-xs font-medium text-brand hover:bg-amber-50"
          aria-expanded={open}
          aria-label={title}
        >
          <FaInfoCircle className="h-3.5 w-3.5 shrink-0" />
          Formulas &amp; examples
        </button>
      </div>
      {modal}
    </>
  );
}

export const HISTORICAL_AVG_FORMULAS = [
  {
    label: 'W-1 / W-2 / W-3 Qty',
    formula: 'Weekly sold quantity of the SKU from Daily Sales for that week.',
    example: 'If week sold 10 kg → W Qty = 10',
  },
  {
    label: 'Avg Qty',
    formula: '(W-1 Qty + W-2 Qty + W-3 Qty) ÷ 3',
    example: '(10 + 12 + 8) ÷ 3 = 10',
  },
  {
    label: 'UPT (Units Per Thousand)',
    formula: '(Weekly Qty ÷ Guest Count) × 1000  (from Daily Sales when available)',
    example: 'Qty 20, Guest Count 500 → UPT = (20 ÷ 500) × 1000 = 40',
  },
  {
    label: 'Avg UPT',
    formula: '(UPT W-1 + UPT W-2 + UPT W-3) ÷ 3',
    example: '(40 + 36 + 44) ÷ 3 = 40',
  },
  {
    label: 'W-1 / W-2 / W-3 Revenue',
    formula: 'Weekly sales amount (revenue) of the SKU from Daily Sales.',
    example: 'If bill amount for week = ₹2,400 → W Rev = 2400',
  },
  {
    label: 'Total Revenue',
    formula: 'W-1 Rev + W-2 Rev + W-3 Rev',
    example: '2400 + 2100 + 2700 = 7200',
  },
];

export const ACTUAL_GC_SALES_FORMULAS = [
  {
    label: 'Sales (per week)',
    formula: 'Sum of selected SKUs’ weekly revenue for that week.',
    example: 'SKU A ₹1000 + SKU B ₹400 = Sales W-1 = 1400',
  },
  {
    label: 'GC (per week)',
    formula: 'For each SKU with UPT: (Qty × 1000) ÷ UPT. Then average those SKU GC values for the week.',
    example: 'Qty 20, UPT 40 → GC = (20 × 1000) ÷ 40 = 500',
  },
  {
    label: 'AVG Sales',
    formula: '(Sales W-1 + Sales W-2 + Sales W-3) ÷ 3',
    example: '(1400 + 1600 + 1500) ÷ 3 = 1500',
  },
  {
    label: 'AVG GC',
    formula: '(GC W-1 + GC W-2 + GC W-3) ÷ 3',
    example: '(500 + 520 + 480) ÷ 3 = 500',
  },
];

export const SKU_PLANNING_FORMULAS = [
  {
    label: 'Lead Time / Buffer % / Growth %',
    formula: 'Taken from product (or category) master set on Configure Tool — read-only here.',
    example: 'Lead Time = 15 days, Buffer = 10%, Growth = 20%',
  },
  {
    label: 'SPS 1 / SPS 2 / SPS 3',
    formula: 'Pack sizes from the product master (SPS 1, SPS 2, SPS 3). Read-only here.',
    example: 'SPS 1 = 2, SPS 2 = 10, SPS 3 = 10',
  },
  {
    label: 'SKU Avg Sales',
    formula: '(SKU Rev W-1 + Rev W-2 + Rev W-3) ÷ 3 from Daily Sales history',
    example: '(1000 + 1200 + 800) ÷ 3 = 1000',
  },
  {
    label: 'SKU Avg GC',
    formula: 'Average of weekly GC for that SKU, where GC week = (Qty × 1000) ÷ UPT',
    example: 'GC weeks 500, 480, 520 → Avg GC = 500',
  },
  {
    label: 'Projected Sales (per SKU)',
    formula:
      'Avg Sales × (1 + Growth% ÷ 100), then rounded to the nearest whole number.',
    example: 'Avg Sales 13, Growth 20% → 13 × 1.20 = 15.6 → shown as 16',
  },
  {
    label: 'Projected GC (per SKU)',
    formula: 'Avg GC × (1 + Growth% ÷ 100), then rounded to the nearest whole number.',
    example: 'Avg GC 1, Growth 20% → 1 × 1.20 = 1.2 → shown as 1',
  },
  {
    label: 'Build To',
    formula:
      'AVG UPT × Gc Buffer. Gc Buffer = PGC × (1 + Buffer% ÷ 100), PGC = Projected GC ÷ 1000. Same as Excel T × W.',
    example: 'AVG UPT 15, Projected GC 761.57, Buffer 5% → PGC 0.762, Gc Buffer 0.80, Build To 15 × 0.80 = 12',
  },
  {
    label: 'Closing Stock',
    formula: 'Qty from closing stock CSV uploaded on Configure Tool for this SKU.',
    example: 'CSV qty 4 → Closing Stock = 4',
  },
  {
    label: 'Suggested Order',
    formula: 'Build To − Closing Stock. Same as Excel X − Y.',
    example: 'Build To 12, Closing Stock 0 → Suggested Order 12. Closing 4 → 12 − 4 = 8',
  },
  {
    label: 'Final Order',
    formula: 'max(0, Suggested Order), rounded to the nearest whole number.',
    example: 'Suggested Order 9 → Final Order 9. If negative, Final Order = 0',
  },
  {
    label: 'Final Order by Outlet',
    formula:
      'Defaults to Final Order. You can edit this value per SKU; the edited value is kept. SPS Ord uses this number.',
    example: 'Final Order 11 → Final Order by Outlet starts as 11; you may change it to 12',
  },
  {
    label: 'SPS Ord 1 / 2 / 3',
    formula:
      'Final Order by Outlet is split across pack sizes, largest SPS first. Each column gets ROUNDDOWN(remaining ÷ that SPS); leftover goes to the next smaller pack. Empty / 0 SPS shows —.',
    example:
      'Final Order by Outlet 11, SPS 1 = 1, SPS 2 = 2, SPS 3 = 0.5 → SPS 2 takes 5 (10), leftover 1 goes to SPS 1. Result: 1, 5, 0. Qty 1 goes only to the pack that fits 1.',
  },
];

export const OVERALL_PROJECTED_FORMULAS = [
  {
    label: 'Overall Projected Sales',
    formula:
      'Sum of rounded Projected Sales of all selected SKUs that have history (nearest whole number).',
    example: 'SKU A 16 + SKU B 8 = Overall Sales 24',
  },
  {
    label: 'Overall Projected GC',
    formula:
      'Sum of rounded Projected GC of all selected SKUs that have history (nearest whole number).',
    example: 'SKU A 1 + SKU B 1 = Overall GC 2',
  },
  {
    label: 'Editable overall fields',
    formula:
      'Defaults to the rounded calculated sum above; you may override the overall values manually.',
    example: 'Calculated 2000 → you can type 2100 if needed',
  },
];
